import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { GTM_EVENTS } from '../constants/widget-constants';
import { MappedAppointmentServicesDao } from '../db-models/appointment-service-dao';
import { AppointmentsSlotsDao } from '../db-models/free-appointment-dao';
import { GoogleAnalyticsService } from './google-analytics.service';
import { LocalStorageService } from './local-storage.service';
import { UtilService } from './util.service';
import { TaxableLine } from './vat-calculation.util';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {

  protected _mappedServiceIds: MappedAppointmentServicesDao = {};
  appointment_services_uuids: string[] = [];

  get mappedServiceIds(): number[] {
    const mappedServiceIds = [].concat(...Object.values(this._mappedServiceIds).map(customFieldMapeedServices => customFieldMapeedServices.serviceIds));
    return [... new Set(mappedServiceIds)];
  }
  set mappedServiceIds(serviceIdObj: MappedAppointmentServicesDao) {
    for (const customFieldIdKey in serviceIdObj) {
      let mappedServiceIdsArray = [];
      if (serviceIdObj[customFieldIdKey].is_multiple_select) {
        const oldValues = this._mappedServiceIds[customFieldIdKey]?.serviceIds || [];
        mappedServiceIdsArray = [...oldValues, ...serviceIdObj[customFieldIdKey].serviceIds];
      } else {
        mappedServiceIdsArray = serviceIdObj[customFieldIdKey].serviceIds;
      }

      if (this._mappedServiceIds[customFieldIdKey]) {
        this._mappedServiceIds[customFieldIdKey].serviceIds = mappedServiceIdsArray;
      } else {
        this._mappedServiceIds[customFieldIdKey] = {
          serviceIds: mappedServiceIdsArray,
          is_multiple_select: serviceIdObj[customFieldIdKey].is_multiple_select,
        };
      }
      break;
    }
  }

  disableFirstTwoButtonsEvent = new EventEmitter<any>();
  resetSlotSettingsEvent = new EventEmitter<any>();
  resetAppointmentCategoryWiseListEvent = new EventEmitter<void>();
  gtmStartTriggered = false;

  constructor(
    public http: HttpClient,
    protected localStorageService: LocalStorageService,
    private utilService: UtilService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
  }

  resetMappedServiceIds(): void {
    this._mappedServiceIds = {};
  }

  // Form cart item unique id identifier
  public createCartItemId(appointment: AppointmentsSlotsDao, selectedDate: Date): string {
    const workerId: number | string = appointment?.worker?.id;
    let combiServiceWorkerIds: string;
    const shortStart = Number(appointment.short_start);

    if (appointment?.combiServiceWorkersMapping) {
      combiServiceWorkerIds = Object.values(appointment.combiServiceWorkersMapping).join('_');
    }

    return (combiServiceWorkerIds || workerId) + '-' + this.utilService.dateToStr(selectedDate) + '-' + shortStart;
  }

  // Get services by their ids
  public getServicesByIds(
    { serviceIds, returnKey = 'services' }: { serviceIds: number[]; returnKey?: string },
    appointmentServicesAvailable: any
  ): any {
    const res: {
      services: object[], totalDuration: number, totalPrice: number, tax: number, isPriceFromActivated: number,
      taxableLines: TaxableLine[]
    } = {
      services: [],
      totalDuration: 0,
      totalPrice: 0,
      tax: 0,
      isPriceFromActivated: 0,
      taxableLines: []
    };

    for (const i in appointmentServicesAvailable) {
      if (serviceIds.indexOf(appointmentServicesAvailable[i]['id']) !== -1) {
        res.isPriceFromActivated = ((appointmentServicesAvailable[i].is_price_from_activated !== undefined)
          ? appointmentServicesAvailable[i].is_price_from_activated
          : 0);
        res.tax = appointmentServicesAvailable[i].tax ? appointmentServicesAvailable[i].tax.rate : 0;
        res.services.push(appointmentServicesAvailable[i]);

        // One taxable line per matched service — unlike res.tax above (which the last matched
        // service overwrites), this keeps every service's own rate for the VAT calculation.
        const servicePrice = Number(appointmentServicesAvailable[i]['price']);
        const serviceRate = appointmentServicesAvailable[i].tax ? Number(appointmentServicesAvailable[i].tax.rate) : 0;
        if (servicePrice > 0 && serviceRate > 0) {
          res.taxableLines.push({ netCents: Math.round(servicePrice * 100), rateHundredths: Math.round(serviceRate * 100) });
        }

        if (appointmentServicesAvailable[i]['duration']) {
          res.totalDuration += Number(appointmentServicesAvailable[i]['duration'].replace(',', '.').replace(' ', ''));
        }
        if (appointmentServicesAvailable[i]['price'] && appointmentServicesAvailable[i]['price'] > 0) {
          if (res.totalPrice === -1) {
            res.totalPrice = Number(appointmentServicesAvailable[i]['price']);
          } else {
            res.totalPrice += Number(appointmentServicesAvailable[i]['price']);
          }
        } else if (appointmentServicesAvailable[i]['price'] && appointmentServicesAvailable[i]['price'] === -1) {
          if (res.totalPrice > 0) {
            // res.totalPrice += Number(appointmentServicesAvailable[i]['price']);
          } else {
            res.totalPrice = -1;
          }
        }
      }
    }

    switch (returnKey) {
      case 'services':
        return res.services;
      case 'total':
        return {
          duration: res.totalDuration,
          price: res.totalPrice,
          tax: res.tax,
          taxPrice: (res.totalPrice > 0) ? Number((res.totalPrice / 100 * res.tax).toFixed(2)) : 0,
          isPriceFromActivated: res.isPriceFromActivated,
          taxableLines: res.taxableLines
        };
    }
  }

  triggerAppointmentStartEvent(): void {
    if (this.gtmStartTriggered === false) {
      this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.APPOINTMENT_BOOKING_START);
      this.gtmStartTriggered = true;
    }
  }

  triggerCompactWidgetStartEvent(): void {
    if (this.gtmStartTriggered === false) {
      this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.COMPACT_WIDGET_BOOKING_START);
      this.gtmStartTriggered = true;
    }
  }
}
